import { Button } from '@repo/shared-client/components/ui'
import { useTranslation } from '@web/src/lib/intl/react'
import {
  ensureServiceWorkerRegistration,
  isServiceWorkerEnabled,
  SERVICE_WORKER_SCOPE,
} from '@web/src/lib/pwa/service-worker-registration'
import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'

const SW_UPDATE_TOAST_ID = 'service-worker-update'

// Regex pattern for iOS device detection
const IOS_DEVICE_PATTERN = /iPad|iPhone|iPod/

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

type DisplayState = 'visible' | 'hidden' | 'frozen' | 'terminated' | 'discarded'

type DisplayStateChangeEvent = {
  newState: DisplayState
  previousState: DisplayState
  timestamp: number
}

interface ShareData {
  title?: string
  text?: string
  url: string
}

type PwaContextType = {
  isInstallable: boolean
  isInstalled: boolean
  installApp: () => Promise<boolean>
  isOnline: boolean
  checkOnlineStatus: () => void
  isIOS: boolean
  displayState: DisplayState
  lastStateChangeTime: number
  lastDisplayStateChange: DisplayStateChangeEvent | null
  onDisplayStateChange: (callback: (event: DisplayStateChangeEvent) => void) => void
  isShareSupported: boolean
  shareContent: (data: ShareData) => Promise<boolean>
}

const PwaContext = createContext<PwaContextType>({
  isInstallable: false,
  isInstalled: false,
  installApp: async () => false,
  isOnline: true,
  checkOnlineStatus: () => {
    /* noop */
  },
  isIOS: false,
  displayState: 'visible',
  lastStateChangeTime: Date.now(),
  lastDisplayStateChange: null,
  onDisplayStateChange: () => {
    /* noop */
  },
  isShareSupported: false,
  shareContent: async () => false,
})

export const PwaProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [isInstallable, setIsInstallable] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)
  const { t } = useTranslation()
  // `navigator.onLine` is undefined during SSR; read the real value after hydration
  const [isOnline, setIsOnline] = useState(true)
  const [isIOS, setIsIOS] = useState(false)
  const [displayState, setDisplayState] = useState<DisplayState>('visible')
  const [lastStateChangeTime, setLastStateChangeTime] = useState(Date.now())
  const [lastDisplayStateChange, setLastDisplayStateChange] = useState<DisplayStateChangeEvent | null>(null)

  // Use a ref to store the callback function
  const displayStateChangeCallbackRef = useRef<((event: DisplayStateChangeEvent) => void) | null>(null)

  // Function to update display state and trigger callback
  const updateDisplayState = (newState: DisplayState) => {
    if (newState !== displayState) {
      const timestamp = Date.now()
      const previousState = displayState

      // Create the event object
      const event: DisplayStateChangeEvent = {
        newState,
        previousState,
        timestamp,
      }

      // Update state
      setDisplayState(newState)
      setLastStateChangeTime(timestamp)
      setLastDisplayStateChange(event)

      // Call the callback if it exists
      if (displayStateChangeCallbackRef.current) {
        try {
          displayStateChangeCallbackRef.current(event)
        } catch (error) {
          console.error('Error in display state change callback:', error)
        }
      }
    }
  }

  // Method to set the callback function
  const onDisplayStateChange = (callback: (event: DisplayStateChangeEvent) => void) => {
    displayStateChangeCallbackRef.current = callback
  }

  useEffect(() => {
    setIsOnline(navigator.onLine)

    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true)
    }

    // Check if device is iOS
    // iPadOS reports a Mac user agent; only touch screens tell it apart from a real Mac
    const isIOSDevice =
      IOS_DEVICE_PATTERN.test(navigator.userAgent) ||
      (navigator.userAgent.includes('Mac') && navigator.maxTouchPoints > 1)
    setIsIOS(isIOSDevice)

    // Global error handler for dynamic import failures
    const handleDynamicImportError = (event: ErrorEvent) => {
      const errorMessage = event.message || ''

      // Check if this is a dynamic import error
      if (
        errorMessage.includes('Failed to fetch dynamically imported module') ||
        errorMessage.includes('error loading dynamically imported module') ||
        errorMessage.includes('Importing a module script failed')
      ) {
        console.warn('Dynamic import failed, reloading page...', errorMessage)

        // Prevent the error from propagating
        event.preventDefault()

        // Reload the page to get the new version
        window.location.reload()
      }
    }

    // Also handle promise rejections that might come from dynamic imports
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason

      // Check if this is a dynamic import error
      if (
        reason &&
        (reason.message?.includes('Failed to fetch dynamically imported module') ||
          reason.message?.includes('error loading dynamically imported module') ||
          reason.message?.includes('Importing a module script failed'))
      ) {
        console.warn('Dynamic import failed (unhandled rejection), reloading page...', reason)

        // Prevent the error from propagating
        event.preventDefault()

        // Reload the page to get the new version
        window.location.reload()
      }
    }

    window.addEventListener('error', handleDynamicImportError)
    window.addEventListener('unhandledrejection', handleUnhandledRejection)

    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent Chrome 76+ from automatically showing the prompt
      e.preventDefault()
      // Store the event for later use
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      setIsInstallable(true)
    }

    const handleAppInstalled = () => {
      // Clear the deferredPrompt variable
      setDeferredPrompt(null)
      setIsInstallable(false)
      setIsInstalled(true)
    }

    const handleOnlineStatusChange = () => {
      setIsOnline(navigator.onLine)
    }

    // Display state change handlers
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        updateDisplayState('visible')

        // Check for service worker updates when user returns to the app
        if (isServiceWorkerEnabled()) {
          navigator.serviceWorker
            .getRegistration(SERVICE_WORKER_SCOPE)
            .then((registration) => registration?.update())
            .catch((error) => {
              console.error('Error checking for service worker updates:', error)
            })
        }
      } else if (document.visibilityState === 'hidden') {
        updateDisplayState('hidden')
      }
    }

    const handlePageShow = () => {
      updateDisplayState('visible')
    }

    const handleFocus = () => {
      updateDisplayState('visible')
    }

    const handleBlur = () => {
      updateDisplayState('hidden')
    }

    const handleResume = () => {
      updateDisplayState('visible')
    }

    const handleFreeze = () => {
      updateDisplayState('frozen')
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)
    window.addEventListener('online', handleOnlineStatusChange)
    window.addEventListener('offline', handleOnlineStatusChange)

    // Display state event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('pageshow', handlePageShow)
    window.addEventListener('focus', handleFocus)
    window.addEventListener('blur', handleBlur)
    window.addEventListener('resume', handleResume)

    // Page lifecycle API events if available
    if ('onfreeze' in document) {
      document.addEventListener('freeze', handleFreeze)
    }

    // Listen for messages from service worker
    let handleServiceWorkerMessage: ((event: MessageEvent) => void) | null = null
    if ('serviceWorker' in navigator) {
      handleServiceWorkerMessage = (event: MessageEvent) => {
        if (event.data && event.data.type === 'ONLINE_STATUS_RESULT') {
          setIsOnline(event.data.isOnline)
        }
      }

      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage)
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
      window.removeEventListener('online', handleOnlineStatusChange)
      window.removeEventListener('offline', handleOnlineStatusChange)
      window.removeEventListener('error', handleDynamicImportError)
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)

      // Clean up display state event listeners
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('pageshow', handlePageShow)
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('blur', handleBlur)
      window.removeEventListener('resume', handleResume)

      if ('onfreeze' in document) {
        document.removeEventListener('freeze', handleFreeze)
      }

      if ('serviceWorker' in navigator && handleServiceWorkerMessage) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayState]) // Added displayState as a dependency

  // A new worker waits until the user picks when to reload, so in-progress work is not lost
  useEffect(() => {
    if (!isServiceWorkerEnabled()) {
      return
    }

    let cancelled = false
    let reloading = false
    let registration: ServiceWorkerRegistration | null = null
    // Without a controller at load, the next controller change is the first install claiming this page
    const hadController = Boolean(navigator.serviceWorker.controller)

    const promptForUpdate = () => {
      const waitingWorker = registration?.waiting
      // Without a controller this is the first install, not an update
      if (!(waitingWorker && navigator.serviceWorker.controller)) {
        return
      }

      toast(
        (updateToast) => (
          <span className="flex items-center gap-3">
            {t('A new version of Spicy Friends is available')}
            <Button
              onClick={() => {
                toast.dismiss(updateToast.id)
                waitingWorker.postMessage({ type: 'SKIP_WAITING' })
              }}
              size="sm"
              type="button"
            >
              {t('Reload')}
            </Button>
          </span>
        ),
        { id: SW_UPDATE_TOAST_ID, duration: Number.POSITIVE_INFINITY }
      )
    }

    const handleUpdateFound = () => {
      const installingWorker = registration?.installing
      installingWorker?.addEventListener('statechange', () => {
        if (installingWorker.state === 'installed') {
          promptForUpdate()
        }
      })
    }

    // A new worker takes over every tab once any tab picks "Reload", and the old precached chunks are
    // gone, so every tab that was on the old version reloads
    const handleControllerChange = () => {
      if (!hadController || reloading) {
        return
      }
      reloading = true
      window.location.reload()
    }

    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange)

    ensureServiceWorkerRegistration()
      .then((activeRegistration) => {
        if (cancelled) return
        registration = activeRegistration
        registration.addEventListener('updatefound', handleUpdateFound)
        promptForUpdate()
      })
      .catch((error: unknown) => {
        console.error('Error watching for service worker updates:', error)
      })

    return () => {
      cancelled = true
      registration?.removeEventListener('updatefound', handleUpdateFound)
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange)
    }
  }, [t])

  const installApp = async () => {
    if (!deferredPrompt) {
      return false
    }

    // Show the install prompt
    deferredPrompt.prompt()

    // Wait for the user to respond to the prompt
    const choiceResult = await deferredPrompt.userChoice

    // Clear the deferredPrompt variable
    setDeferredPrompt(null)
    setIsInstallable(false)

    return choiceResult.outcome === 'accepted'
  }

  const checkOnlineStatus = () => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'ONLINE_STATUS_CHECK',
      })
    } else {
      setIsOnline(navigator.onLine)
    }
  }

  // Check if Web Share API is supported
  const isShareSupported = typeof navigator !== 'undefined' && 'share' in navigator

  // Share content using Web Share API
  const shareContent = async (data: ShareData): Promise<boolean> => {
    if (!isShareSupported) {
      return false
    }

    try {
      await navigator.share({
        title: data.title,
        text: data.text,
        url: data.url,
      })
      return true
    } catch (error) {
      // User cancelled or error occurred
      if ((error as Error).name !== 'AbortError') {
        console.error('Error sharing content:', error)
      }
      return false
    }
  }

  return (
    <PwaContext.Provider
      value={{
        isInstallable,
        isInstalled,
        installApp,
        isOnline,
        checkOnlineStatus,
        isIOS,
        displayState,
        lastStateChangeTime,
        lastDisplayStateChange,
        onDisplayStateChange,
        isShareSupported,
        shareContent,
      }}
    >
      {children}
    </PwaContext.Provider>
  )
}

export const usePwa = () => useContext(PwaContext)
